import React, { useState } from "react";
import styles from "./index.module.css";
import OrderBreakdown from "../OrderBreakdown";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import moment from "moment";
import { isCatalyst, catalystRenewalInfoText } from "../../utils/brandConfig";

const OrderSummary = ({
  order,
  isSelfCheckout,
  selfCheckoutDetails,
  maxExpiryDate,
  addOnDetails,
  businessUnitId,
}) => {
  const catalystMode = isCatalyst(businessUnitId);
  const idEnums = {
    parentName: "parentName",
    courses: "courses",
    childrenEnrolled: "childrenEnrolled",
    credits: "credits",
    creditsExpiry: "creditsExpiry",
  };

  // Get parent name from appropriate source
  const parentName = isSelfCheckout
    ? selfCheckoutDetails?.parentName
    : order?.[0]?.parentName;

  // Calculate total sessions/credits
  const totalSessions = isSelfCheckout
    ? selfCheckoutDetails?.totalSessionsPurchased
    : order?.reduce((acc, ord) => acc + (ord?.session || 0), 0);

  // Get children count
  const childrenCount = order?.length || 0;

  // Extract course names for Catalyst mode
  const courseNames = catalystMode
    ? order
        ?.map((childOrder) =>
          childOrder?.coursesInfo?.map((course) => course?.courseName),
        )
        .flat()
        .filter(Boolean)
        .join(", ")
    : "";

  const isFlex = order?.[0]?.coursesInfo?.[0]?.isFlex || false; // All courses have same type, so checking the first one is sufficient
  const flexMonths = order?.[0]?.coursesInfo?.[0]?.duration || null;

  // Build order details dynamically
  const orderDetails = [
    {
      id: idEnums.parentName,
      isVisible: !!parentName,
      title: "Parent name",
      value: parentName || "",
    },
    {
      id: idEnums.courses,
      isVisible: catalystMode && !!courseNames,
      title: "Courses",
      value: courseNames,
    },
    {
      id: idEnums.childrenEnrolled,
      isVisible: !isSelfCheckout && childrenCount > 1,
      title: "Children enrolled",
      value: `${childrenCount} ${childrenCount === 1 ? "child" : "children"}`,
    },
    {
      id: idEnums.credits,
      isVisible: !!totalSessions && !isFlex,
      title: catalystMode || isSelfCheckout ? "Total sessions" : "Credits",
      value:
        catalystMode || isSelfCheckout
          ? `${totalSessions} classes`
          : `${totalSessions} class credits`,
      clubPassesValue: catalystMode
        ? null
        : addOnDetails?.totalAddonSessions || null,
      showClubPasses: catalystMode
        ? false
        : addOnDetails?.totalAddonSessions > 0,
      isClubPassFree: catalystMode ? false : addOnDetails?.isClubPassFree,
      freeClubPassCredits: catalystMode
        ? 0
        : addOnDetails?.freeClubPassCredits || 0,
    },
    {
      id: idEnums.creditsExpiry,
      isVisible: catalystMode || !!maxExpiryDate,
      title: `${isFlex ? "Course duration" : "Credits expiry"}`,
      value: catalystMode
        ? ""
        : isFlex
          ? `${flexMonths} month${flexMonths > 1 ? "s" : ""}`
          : maxExpiryDate
            ? moment(maxExpiryDate).format("DD MMM YYYY")
            : "",
      hideValue: catalystMode,
    },
  ];
  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpenBreakdown = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseBreakdown = () => {
    setAnchorEl(null);
  };

  return (
    <div className={styles.orderSummary}>
      <p className={styles.title}>Order summary</p>
      <div className={styles.orderDetails}>
        {orderDetails?.map((detail) => {
          if (detail?.isVisible) {
            return (
              <div key={detail?.id} className={styles.detailRow}>
                <span className={styles.detailTitle}>{detail?.title}</span>
                {!detail?.hideValue && (
                  <span className={styles.detailValue}>{detail?.value}</span>
                )}
                {detail?.showClubPasses && (
                  <div>
                    <span className={`${styles.detailValue}`}>
                      {detail?.clubPassesValue} club passes
                    </span>
                    {detail?.isClubPassFree && (
                      <span className={styles.freeClubPass}>Free</span>
                    )}
                  </div>
                )}
                {detail?.freeClubPassCredits > 0 && (
                  <div>
                    <span className={`${styles.detailValue}`}>
                      {detail?.freeClubPassCredits} club passes
                    </span>
                    <span className={styles.freeClubPass}>Free</span>
                  </div>
                )}
                {detail?.id === idEnums?.creditsExpiry && (
                  <span className={styles.renewalInfo}>
                    {catalystMode
                      ? catalystRenewalInfoText
                      : isFlex
                        ? ""
                        : "*Expiry dates vary by subject"}
                  </span>
                )}
              </div>
            );
          }
          return null;
        })}
      </div>
      {!catalystMode && !isSelfCheckout && order?.length > 0 && (
        <div className={styles.viewBreakdown} onClick={handleOpenBreakdown}>
          <span>View breakdown</span>
          {anchorEl ? (
            <KeyboardArrowUpIcon className={styles.arrowIcon} />
          ) : (
            <KeyboardArrowDownIcon className={styles.arrowIcon} />
          )}
        </div>
      )}
      {!catalystMode && (
        <OrderBreakdown
          anchorEl={anchorEl}
          onClose={handleCloseBreakdown}
          order={order}
          isSelfCheckout={isSelfCheckout}
          selfCheckoutDetails={selfCheckoutDetails}
          isFlex={isFlex}
        />
      )}
    </div>
  );
};

export default OrderSummary;
